import React from 'react';
import type { HeaderConfig } from '@/components/Print/UnifiedPrintModal';

// Shared by the printed prescription and invoice: black ink only, 10pt body, nothing under 8pt.
// Rendered with renderToStaticMarkup into the print iframe, so styles travel as a <style> block.
// min-height keeps the signature near the foot of a one-page document: A4 minus the html + body padding, with room left for printer margins (a custom 1" top margin still fits one sheet).
export const PRINT_CSS = `
.p-doc { font-size: 13.3px; line-height: 1.45; color: #111; display: flex; flex-direction: column; min-height: 220mm; }
.p-muted { color: #555; }
.p-small { font-size: 11.5px; }
.p-lh { display: flex; justify-content: space-between; align-items: flex-end; gap: 12mm; padding-bottom: 4mm; border-bottom: 1.5px solid #111; }
.p-lh-clinic { font-size: 22px; font-weight: 700; letter-spacing: -0.01em; }
.p-lh-doc { text-align: right; }
.p-lh-doc b { font-size: 15px; }
.p-lh-custom { text-align: center; white-space: pre-wrap; padding-bottom: 4mm; border-bottom: 1.5px solid #111; }
.p-lh-brand { display: flex; align-items: center; gap: 4mm; }
.p-lh-logo { height: 16mm; width: auto; max-width: 40mm; object-fit: contain; }
.p-lh-img { display: block; width: 100%; max-height: 45mm; object-fit: contain; margin-bottom: 2mm; }
.p-table { width: 100%; border-collapse: collapse; }
.p-table th { text-align: left; font-size: 11px; font-weight: 700; color: #555; padding: 1.5mm 2mm; border-bottom: 1px solid #111; }
.p-table td { padding: 2.2mm 2mm; border-bottom: 1px solid #d6d6d6; vertical-align: top; }
.p-table .p-num { text-align: right; font-variant-numeric: tabular-nums; white-space: nowrap; }
.p-sign { margin-top: auto; padding-top: 14mm; display: flex; justify-content: flex-end; page-break-inside: avoid; }
.p-sign > div { min-width: 62mm; border-top: 1px solid #111; padding-top: 1.5mm; text-align: center; font-size: 12px; }
.p-sign b { font-size: 13.5px; }
.p-sign > .p-sign-col { border-top: none; padding-top: 0; }
.p-sign-col > div { border-top: 1px solid #111; padding-top: 1.5mm; }
.p-sign-img { display: block; max-height: 16mm; max-width: 55mm; margin: 0 auto 1mm; }
.p-foot { margin-top: 6mm; padding-top: 2mm; border-top: 1px solid #d6d6d6; display: flex; justify-content: space-between; gap: 6mm; font-size: 10.5px; color: #555; }
`;

/** "99700 87263", as on screen */
export const groupPhone = (phone: string) => phone.replace(/^(\d{5})(\d{5})$/, '$1 $2');

export const PrintStyles =() => <style dangerouslySetInnerHTML={{ __html: PRINT_CSS }} />;

interface LetterheadProps {
  headerConfig?: HeaderConfig;
  /** logo and letterhead: data URLs of the clinic's images, when uploaded in Settings */
  clinic: { name?: string; address?: string; phone?: string; logo?: string | null; letterhead?: string | null };
  /** Right-hand block: the doctor on a prescription, GSTIN on an invoice */
  right?: React.ReactNode;
}

/**
 * "Clinic header" mode: logo and clinic left, doctor right. "Letterhead image" prints the clinic's uploaded banner.
 * "No header" leaves blank space for pre-printed letterhead; "Custom" prints the clinic's own text.
 */
export function Letterhead({ headerConfig, clinic, right }: LetterheadProps) {
  const mode = headerConfig?.mode ?? 'auto';
  if (mode === 'none') return <div style={{ height: `${headerConfig!.topSpaceMm}mm` }} />;
  if (mode === 'custom') return headerConfig!.customText ? <div className="p-lh-custom">{headerConfig!.customText}</div> : null;
  // No banner uploaded (or removed since): fall back to the clinic header
  if (mode === 'image' && clinic.letterhead) return <img className="p-lh-img" src={clinic.letterhead} alt="" />;

  const show = (flag: keyof HeaderConfig) => !headerConfig || headerConfig[flag];
  const contact = [show('showAddress') && clinic.address, show('showPhone') && clinic.phone && `Ph ${groupPhone(clinic.phone)}`].filter(Boolean).join(' · ');
  if (!clinic.name && !right && !clinic.logo) return null;
  return (
      <div className="p-lh">
        <div className="p-lh-brand">
          {clinic.logo && <img className="p-lh-logo" src={clinic.logo} alt="" />}
          <div>
            {show('showName') && clinic.name && <div className="p-lh-clinic">{clinic.name}</div>}
            {contact && <div className="p-small p-muted">{contact}</div>}
          </div>
        </div>
        {right && <div className="p-lh-doc">{right}</div>}
      </div>
  );
}

/** Signature line at the foot of the page; image is the doctor's signature, printed just above the line */
export function SignatureBlock({ children, image }: { children: React.ReactNode; image?: string | null }) {
  if (!image) return <div className="p-sign"><div>{children}</div></div>;
  return (
    <div className="p-sign">
      <div className="p-sign-col">
        <img className="p-sign-img" src={image} alt="" />
        <div>{children}</div>
      </div>
    </div>
  );
}

export function PrintFooter({ left, printedOn }: { left: string; printedOn: string }) {
  return <div className="p-foot"><span>{left}</span><span>Printed {printedOn}</span></div>;
}
